import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { cn } from "@/lib/cn";

type Props = {
  /** Valor no formato "YYYY-MM-DD" (vazio se não tem data). */
  value: string;
  onChange: (ymd: string) => void;
  minAge?: number;
  maxAge?: number;
  className?: string;
  placeholder?: string;
};

function parseYmdLocal(ymd: string): Date | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Seletor de data de nascimento — input nativo + botão de calendário
 *  abrindo popover com Calendar shadcn + dropdowns de mês/ano pra navegação
 *  rápida de décadas. */
export function BirthdayPicker({
  value,
  onChange,
  minAge = 0,
  maxAge = 120,
  className,
  placeholder,
}: Props) {
  const [open, setOpen] = useState(false);
  const thisYear = new Date().getFullYear();
  const startMonth = new Date(thisYear - maxAge, 0, 1);
  const endMonth = new Date(thisYear - minAge, 11, 31);

  const parsed = parseYmdLocal(value);

  return (
    <div className={cn("relative", className)}>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2.5 pr-9 text-sm text-zinc-900 outline-none transition-colors placeholder:text-zinc-400 focus:border-brand-400 focus:ring-1 focus:ring-brand-400 dark:border-border dark:bg-card dark:text-foreground dark:placeholder:text-muted-foreground"
        min={toYmd(startMonth)}
        max={toYmd(endMonth)}
      />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Abrir calendário"
            className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-primary/10 dark:hover:text-primary"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-[320px] overflow-hidden rounded-xl border border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
        >
          {/* Header: título + data selecionada com destaque */}
          <div className="border-b border-zinc-100 bg-gradient-to-b from-brand-50/40 to-white px-4 py-3 dark:border-border dark:from-primary/5 dark:to-card">
            <p className="text-[10px] font-bold uppercase tracking-wider text-brand-700 dark:text-primary">
              Data de nascimento
            </p>
            {parsed ? (
              <p className="mt-0.5 text-[13px] font-semibold text-zinc-900 dark:text-foreground">
                {format(parsed, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </p>
            ) : (
              <p className="mt-0.5 text-[11.5px] text-zinc-500 dark:text-muted-foreground">
                Use os dropdowns de mês e ano pra navegar rápido
              </p>
            )}
          </div>

          {/* Calendar com dropdowns de mês/ano no caption */}
          <div className="p-2">
            <Calendar
              mode="single"
              selected={parsed ?? undefined}
              onSelect={(d) => {
                if (!d) return;
                onChange(toYmd(d));
                setOpen(false);
              }}
              captionLayout="dropdown"
              startMonth={startMonth}
              endMonth={endMonth}
              defaultMonth={parsed ?? new Date(thisYear - 30, 0, 1)}
              weekStartsOn={0}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
